import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import copy from 'copy-to-clipboard';
import { USER_TYPES } from '@medflex/utils/constants';
import PopupMenu from '@medflex/components/stories/PopupMenu';
import ChatStream from '@medflex/components/stories/ChatStream';
import LoadingView from '@medflex/components/stories/LoadingView';
import ActionSheet from '@medflex/components/stories/ActionSheet';
import ChatMessageStreamHeader from '@medflex/components/stories/ChatMessageStreamHeader';
import {
  IconNavigationMore,
  IconTrash,
  IconExclamationSolid,
  IconViewShow,
  IconCopy,
  IconDownload,
  IconCheckmark,
} from '@medflex/components/stories/Icons';
import { IdentificationIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';
import Button from '@medflex/components/stories/Button';
import { showAlertToast, showSuccessToast } from '@medflex/components/stories/Toast';
import useUserSession from '@medflex/hooks/useUserSession';
import { TRACKING_ACTIONS, TRACKING_ELEMENT_TYPES, trackEvent } from '@medflex/utils/Tracking';
import config from '@medflex/utils/config';
import isAudioFiles from '@medflex/utils/isAudioFiles';
import isWebView from '@medflex/utils/isWebView';
import useIsWideScreen from '@medflex/hooks/useIsWideScreen';
import { downloadMp3, open as openFile, download } from '@medflex/utils/handleFile';
import { MessageTypes } from '@medflex/shared-types';
import isPatientUserId from '@medflex/utils/isPatientUserId';
import { format } from '@medflex/i18n';
import { gdtConstants } from '@medflex/gdt';
import useSelectedContact from '../hooks/useSelectedContact';
import useLastReadMessageIdOfSelectedContact from '../hooks/useLastReadMessageIdOfSelectedContact';
import useMergedMessages from '../hooks/useMergedMessages';
import useUserProfile from '../hooks/useUserProfile';
import useChatErrors from '../hooks/useChatErrors';
import useMarkMessagesAsRead from '../hooks/useMarkMessagesAsRead';
import useSelectedContactId from '../hooks/useSelectedContactId';
import { useAcceptSharedPatientContact } from '../hooks/useShareContact';
import useMessages from '../hooks/useMessages';
import useModal from '../hooks/useModal';
import isSelf from '../../utils/isSelf';
import isSubContactId from '../../utils/isSubContactId';
import isSupportPatient from '../../utils/isSupportPatient';
import useSelectedMainContact from '../hooks/useSelectedMainContact';
import useChatContacts from '../hooks/useChatContacts';
import useSessionMeetings from '../hooks/useSessionMeetings';
import useGdt from '../hooks/useGdt';

const isMessage = ({ type = 'message' } = {}) => type === 'message';

const MessageActionMenu = ({ messageId, children, hidden, onClickOutside, markRead }) => {
  const { t } = useTranslation();
  const { open } = useModal();
  const { messages, markImportant, markChecked } = useMessages();
  const contactId = useSelectedContactId();
  const { selectedContact } = useSelectedContact();
  const isWideScreen = useIsWideScreen();
  const { userProfile } = useUserProfile();
  const { session: userSession } = useUserSession();
  const { onWriteExamination, canWriteExamination } = useGdt(selectedContact?.pvsId);

  const message = useMemo(() => messages.find(e => e.id === messageId), [messages]);

  const isImportant = useMemo(
    () => message && typeof message.markedImportantAt !== 'undefined',
    [message],
  );

  const isChecked = useMemo(() => message && typeof message.checkedAt !== 'undefined', [message]);

  const isSender = useMemo(() => {
    const { id } = message.sender;
    return (
      (message && id === userProfile.id) ||
      (isSubContactId(id) && userProfile.type === USER_TYPES.PATIENT)
    );
  }, [message]);

  const isAudioMessage = useMemo(() => {
    if (!message || !message.files) {
      return false;
    }
    return isAudioFiles(message.files);
  }, [message]);

  const markUnread = async event => {
    const selectedMessageIndex = messages.findIndex(msg => msg.id === messageId);
    const lastReadMessage = messages[selectedMessageIndex - 1];
    await markRead({
      contactId,
      ...(lastReadMessage && { id: lastReadMessage.id }),
    });
    trackEvent(event);
  };

  const toggleImportant = async event => {
    await markImportant({
      id: messageId,
      important: !isImportant,
    });
    trackEvent(event);
  };

  const toggleChecked = async event => {
    await markChecked({
      id: messageId,
      checked: !isChecked,
    });
    trackEvent(event);
  };

  useEffect(() => {
    document.addEventListener('click', onClickOutside);
    return () => {
      document.removeEventListener('click', onClickOutside);
    };
  }, []);

  const copyText = text => {
    copy(text);
    showSuccessToast(t('ChatMessageStream.action_menu.copied'), null, {
      toastId: 'chatMessageCopied',
    });
  };

  const downloadFile = async (event, audioFile) => {
    const isSuccess = await downloadMp3({
      uri: `${config.INSTANT_MESSAGE_SERVICE_ENDPOINT}/files/${audioFile.id}`,
      name: audioFile.name,
      token: userSession.access_token,
    });

    if (!isSuccess) {
      showAlertToast(
        t('ChatMessageStream.action_menu.download_failed', null, { toastId: 'fileDownloadFailed' }),
      );
      return;
    }

    showSuccessToast(t('ChatMessageStream.action_menu.downloaded'), null, {
      toastId: 'fileDonwloaded',
    });

    const payload = {
      action: TRACKING_ACTIONS.DOWNLOAD,
      elementType: TRACKING_ELEMENT_TYPES.BUTTON,
      actionText: 'download',
    };
    trackEvent(event, payload);
  };

  if (isWideScreen && !hidden) {
    return (
      <PopupMenu>
        <PopupMenu.Menu>
          <PopupMenu.Group>
            <PopupMenu.Item onClick={markUnread}>
              <IconViewShow size="5" />
              <span className="ml-3">{t('ChatMessageStream.action_menu.unread')}</span>
            </PopupMenu.Item>
            <PopupMenu.Item onClick={toggleImportant}>
              <IconExclamationSolid size="5" />
              <span className="ml-3">
                {isImportant
                  ? t('ChatMessageStream.action_menu.not_important')
                  : t('ChatMessageStream.action_menu.important')}
              </span>
            </PopupMenu.Item>
            {isSender && message.type !== 'welcome' && (
              <PopupMenu.Item onClick={() => open({ modal: 'deleteMessage', messageId })}>
                <IconTrash size="5" />
                <span className="ml-3">{t('ChatMessageStream.action_menu.delete')}</span>
              </PopupMenu.Item>
            )}
            {message.text && (
              <PopupMenu.Item onClick={() => copyText(message.text)}>
                <IconCopy size="5" />
                <span className="ml-3">{t('ChatMessageStream.action_menu.copy')}</span>
              </PopupMenu.Item>
            )}
            {isAudioMessage && !isWebView() && (
              <PopupMenu.Item onClick={event => downloadFile(event, message.files[0])}>
                <IconDownload size="5" />
                <span className="ml-3">{t('ChatMessageStream.action_menu.download')}</span>
              </PopupMenu.Item>
            )}
            {!isPatientUserId(userProfile.id) && isPatientUserId(selectedContact.user.id) && (
              <PopupMenu.Item onClick={toggleChecked}>
                <IconCheckmark size="5" />
                <span className="ml-3">
                  {isChecked
                    ? t('ChatMessageStream.action_menu.uncheck')
                    : t('ChatMessageStream.action_menu.check')}
                </span>
              </PopupMenu.Item>
            )}
            {canWriteExamination && (
              <PopupMenu.Item
                onClick={() =>
                  onWriteExamination({
                    text: `${format(message.time, 'DD.MM.YYYY HH:mm:ss')}, ${message.user.fullNameWithTitle}\n${message.text}`,
                    files: message.files,
                    fileSource: gdtConstants.FileSources.CONTACT,
                  })
                }
              >
                <IdentificationIcon className="w-5 h-5" />
                <span className="ml-3">{t('ChatMessageStream.action_menu.write_examination')}</span>
              </PopupMenu.Item>
            )}
          </PopupMenu.Group>
        </PopupMenu.Menu>
        <PopupMenu.Trigger>
          {({ triggerRef }) => (
            // eslint-disable-next-line jsx-a11y/control-has-associated-label
            <button
              type="button"
              onClick={event => {
                event.preventDefault();
                trackEvent(event, {
                  action: TRACKING_ACTIONS.OPEN,
                  elementType: TRACKING_ELEMENT_TYPES.ICON,
                  actionText: '•••',
                });
              }}
              ref={triggerRef}
              className="hover:bg-slate-300 m-1 p-1 focus:outline-none rounded-lg text-slate-600 w-8 h-8 flex justify-center items-center flex-shrink-0 cursor-pointer"
            >
              <IconNavigationMore />
            </button>
          )}
        </PopupMenu.Trigger>
      </PopupMenu>
    );
  }
  if (!isWideScreen) {
    return (
      <ActionSheet initialVisible={false}>
        <ActionSheet.Menu closeButtonLabel={t('global:actions.close_button')}>
          <ActionSheet.Item onClick={markUnread}>
            <IconViewShow size="5" />
            <span className="ml-3">{t('ChatMessageStream.action_menu.unread')}</span>
          </ActionSheet.Item>
          <ActionSheet.Item onClick={toggleImportant}>
            <IconExclamationSolid size="5" />
            <span className="ml-3">
              {isImportant
                ? t('ChatMessageStream.action_menu.not_important')
                : t('ChatMessageStream.action_menu.important')}
            </span>
          </ActionSheet.Item>
          {isSender && message.type !== 'welcome' && (
            <ActionSheet.Item onClick={() => open({ modal: 'deleteMessage', messageId })}>
              <IconTrash size="5" />
              <span className="ml-3">{t('ChatMessageStream.action_menu.delete')}</span>
            </ActionSheet.Item>
          )}
          {message.text && (
            <ActionSheet.Item onClick={() => copyText(message.text)}>
              <IconCopy size="5" />
              <span className="ml-3">{t('ChatMessageStream.action_menu.copy')}</span>
            </ActionSheet.Item>
          )}
          {isAudioMessage && !isWebView() && (
            <ActionSheet.Item onClick={event => downloadFile(event, message.files[0])}>
              <IconDownload size="5" />
              <span className="ml-3">{t('ChatMessageStream.action_menu.download')}</span>
            </ActionSheet.Item>
          )}
          {!isPatientUserId(userProfile.id) && isPatientUserId(selectedContact.user.id) && (
            <ActionSheet.Item onClick={toggleChecked}>
              <IconCheckmark size="5" />
              <span className="ml-3">
                {isChecked
                  ? t('ChatMessageStream.action_menu.uncheck')
                  : t('ChatMessageStream.action_menu.check')}
              </span>
            </ActionSheet.Item>
          )}
          {canWriteExamination && (
            <ActionSheet.Item
              onClick={() =>
                onWriteExamination({
                  text: `${format(message.time, 'DD.MM.YYYY HH:mm:ss')}, ${message.user.fullNameWithTitle}\n${message.text}`,
                  files: message.files,
                  fileSource: gdtConstants.FileSources.CONTACT,
                })
              }
            >
              <IdentificationIcon className="w-5 h-5" />
              <span className="ml-3">{t('ChatMessageStream.action_menu.write_examination')}</span>
            </ActionSheet.Item>
          )}
        </ActionSheet.Menu>
        <ActionSheet.Trigger>
          {({ setIsVisible, isVisible }) => {
            const toggle = () => {
              setIsVisible(!isVisible);
              trackEvent({
                zone: 'ChatMessageStream_ActionMenu',
                action: TRACKING_ACTIONS.OPEN,
              });
            };
            children({ toggle });
          }}
        </ActionSheet.Trigger>
      </ActionSheet>
    );
  }
  return null;
};

MessageActionMenu.propTypes = {
  messageId: PropTypes.string,
  hidden: PropTypes.bool,
  onClickOutside: PropTypes.func,
  markRead: PropTypes.func,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

const PatientMessageAction = props => {
  const {
    message: { id, patient },
  } = props;
  const { accept, data, errors, isLoading } = useAcceptSharedPatientContact();
  const { contacts: chatContacts } = useChatContacts();
  const { t } = useTranslation('physician');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const history = useHistory();

  const acceptedContact = useMemo(
    () => chatContacts.find(contact => contact.user.id === patient.id),
    [patient.id, chatContacts],
  );

  const handleAccept = () => {
    setIsSubmitted(true);
    accept(id, patient);
  };

  const handleRedirectToChat = () => {
    history.push(`/chat/${acceptedContact.id}`);
  };

  useEffect(() => {
    if (!isLoading && isSubmitted) {
      if (errors && errors.acceptSharedContact) {
        showAlertToast(
          t('chat_stream_module.share_patient_contact.toast.alert.title'),
          t('chat_stream_module.share_patient_contact.toast.alert.description'),
          { toastId: 'sharePatientFailed' },
        );
        setIsSubmitted(false);
        return;
      }

      if (data) {
        trackEvent({
          zone: 'ChatStreamModule',
          action: TRACKING_ACTIONS.ACCEPT,
          elementType: TRACKING_ELEMENT_TYPES.BUTTON,
          actionText: 'Als Kontakt hinzufügen',
        });
        showSuccessToast(
          t('chat_stream_module.share_patient_contact.toast.success.title'),
          t('chat_stream_module.share_patient_contact.toast.success.description', {
            contactId: data.id,
            allowHtml: true,
          }),
          { toastId: 'sharePatientSuccess' },
        );
      }
    }
  }, [data, isSubmitted, isLoading]);

  if (acceptedContact) {
    return (
      <Button kind="primary:outline" isLoading={isLoading} onClick={handleRedirectToChat}>
        {t('chat_stream_module.share_patient_contact.type_message')}
      </Button>
    );
  }

  return (
    <Button isLoading={isLoading} onClick={handleAccept}>
      {t('chat_stream_module.share_patient_contact.accept_button')}
    </Button>
  );
};

PatientMessageAction.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    patient: PropTypes.shape({
      id: PropTypes.string.isRequired,
      firstName: PropTypes.string.isRequired,
      lastName: PropTypes.string.isRequired,
    }),
  }),
};

const DeletionNoteText = () => {
  const { selectedContact } = useSelectedContact();
  const { t } = useTranslation();

  const deletionNoteText = useMemo(() => {
    if (selectedContact && isSelf(selectedContact.user)) return null;
    return t('ChatMessageStream.deletion_note');
  }, [selectedContact, t]);

  return deletionNoteText;
};

const ChatStreamModule = ({ onScheduleDemo, isBasicUser }) => {
  const { autoResponseMessage, isLoading: isLoadingMessages } = useMessages();
  const { selectedContact } = useSelectedContact();
  const mainContact = useSelectedMainContact();
  const { session: userSession } = useUserSession();
  const { userProfile } = useUserProfile();
  const errors = useChatErrors();
  const lastReadMessageId = useLastReadMessageIdOfSelectedContact();
  const { t } = useTranslation();
  const location = useLocation();
  const mergedMessages = useMergedMessages(isPatientUserId(userProfile.id));
  const { onWriteExamination, canWriteExamination } = useGdt(selectedContact?.pvsId);

  const { load: loadMeetings, goTo: goToMeeting } = useSessionMeetings();

  async function* fetchAudioSource({ files: audioFiles }, type = null) {
    const { INSTANT_MESSAGE_SERVICE_ENDPOINT, PAM_API_URL } = config;
    const audioFile = audioFiles[0];
    let rs;

    const options = {
      headers: {
        AUTHORIZATION: `Bearer ${userSession.access_token}`,
      },
    };

    if (type === MessageTypes.PAM) {
      const rs = await fetch(`${PAM_API_URL}/files/${audioFile.id}`, options);

      const file = await rs.json();

      const downloadFile = await fetch(file.attributes.download.url);
      yield window.URL.createObjectURL(await downloadFile.blob());
    }

    rs = await fetch(`${INSTANT_MESSAGE_SERVICE_ENDPOINT}/files/${audioFile.id}/original`, options);
    yield window.URL.createObjectURL(await rs.blob());

    rs = await fetch(`${INSTANT_MESSAGE_SERVICE_ENDPOINT}/files/${audioFile.id}/mp4`, options);
    yield window.URL.createObjectURL(await rs.blob());
  }

  const fileMessageClickHandler = useCallback(
    (file, messageType = null) => {
      openFile({
        uri:
          messageType === MessageTypes.PAM
            ? `${config.PAM_API_URL}/files/${file.id}`
            : `${config.INSTANT_MESSAGE_SERVICE_ENDPOINT}/files/${file.id}`,
        token: userSession.access_token,
      });
    },
    [userSession.access_token],
  );

  const fileMessageDownloadHandler = useCallback(
    file => {
      download({
        uri: `${config.PAM_API_URL}/files/${file.id}`,
        token: userSession.access_token,
        name: file.name,
      });
    },
    [userSession.access_token],
  );

  useEffect(() => {
    loadMeetings();
  }, []);

  const chatUser = useMemo(() => {
    if (selectedContact && mainContact) {
      if (isSelf(selectedContact.user)) {
        return {
          fullNameWithTitle: t('physician:address_book.self_title'),
          isSelf: true,
        };
      }

      if (userProfile.flags.isPatient) {
        return mainContact.user;
      }

      return selectedContact.user;
    }

    return null;
  }, [selectedContact, mainContact, userProfile]);

  const newLastReadMessageId = useMemo(() => {
    // if lastReadMessage is followed by calls, lastReadMessageId has to be modified
    // to id of last call before next normal message
    if (lastReadMessageId && mergedMessages) {
      const lastReadMessageIndex = mergedMessages.findIndex(
        message => message.id === lastReadMessageId,
      );
      const unreadMessages = mergedMessages.slice(lastReadMessageIndex + 1);
      // if no unread messages, no need to change lastReadMessageId
      if (unreadMessages.length === 0) return lastReadMessageId;
      const hasNormalMessages = unreadMessages.filter(isMessage).length > 0;
      // if no normal (not a call) message is unread, set lastReadMessageId to id of last unread call
      if (!hasNormalMessages) return unreadMessages[unreadMessages.length - 1].id;
      const lastCallIndex = unreadMessages.findIndex(isMessage) - 1;
      return (lastCallIndex > -1 && unreadMessages[lastCallIndex].id) || lastReadMessageId;
    }
    // in case there is no lastReadMessageId, set lastReadMessageId to null or the last call
    // before the first normal message
    const lastNormalMessageIndex = mergedMessages.findIndex(isMessage);
    // in case there is no normal message but calls, set newLastReadMessageId to id of last call
    if (lastNormalMessageIndex < 0) {
      return (mergedMessages.length > 0 && mergedMessages[mergedMessages.length - 1].id) || null;
    }
    const callsBeforeFirstNormal = mergedMessages.slice(0, lastNormalMessageIndex);
    return (
      (callsBeforeFirstNormal[callsBeforeFirstNormal.length - 1] &&
        callsBeforeFirstNormal[callsBeforeFirstNormal.length - 1].id) ||
      null
    );
  }, [lastReadMessageId, mergedMessages]);

  const markRead = useMarkMessagesAsRead();

  const getHeaderText = () => {
    if (!selectedContact.enabled) {
      return t('ChatMessageStream.header.text_unpaired', {
        allowHtml: true,
        contactType:
          selectedContact.type === USER_TYPES.PATIENT
            ? t('ChatMessageStream.header.contact_types.patient')
            : t('ChatMessageStream.header.contact_types.physician'),
      });
    }
    if (isSelf(selectedContact.user)) {
      return t('ChatMessageStream.header.text_self', { allowHtml: true });
    }
    if (isSupportPatient(selectedContact.user)) {
      return t('ChatMessageStream.header.text_support', { allowHtml: true });
    }
    return t('ChatMessageStream.header.text_paired', { allowHtml: true });
  };

  if (!mergedMessages || isLoadingMessages) {
    return (
      <LoadingView title={t('chat_room.loading.title')} message={t('chat_room.loading.message')} />
    );
  }

  const deletionNoteUrl = userProfile.flags.isPatient
    ? t('ChatMessageStream.deletion_note_url_patient')
    : t('ChatMessageStream.deletion_note_url_physician');

  // @TODO normally a 404 state should be displayed here
  if (!selectedContact) return null;

  return (
    <ChatStream
      isUserPatient={userProfile.flags.isPatient}
      contact={chatUser}
      userName={userProfile.attributes.fullNameWithTitle}
      newMessagesLabel={t('ChatMessageStream.separator.new_messages')}
      messageTypeLabels={{
        welcome: t('ChatMessageStream.types.welcome'),
        demo: t('ChatMessageStream.types.demo'),
      }}
      messages={mergedMessages || []}
      errors={errors}
      fileMessageClickHandler={fileMessageClickHandler}
      fileMessageDownloadHandler={fileMessageDownloadHandler}
      messageSeenText={t('messenger:message_seen.single')}
      PatientMessageAction={PatientMessageAction}
      lastReadMessageId={newLastReadMessageId}
      anchor={location.hash.slice(1)}
      // eslint-disable-next-line react/no-unstable-nested-components
      MessageActionMenu={() => <MessageActionMenu markRead={markRead} />}
      onSessionParticipate={goToMeeting}
      onScheduleDemo={onScheduleDemo}
      deletedMessageText={t('ChatMessageStream.message.deleted')}
      classNames="px-4 pt-4 lg:px-12 lg:pt-12"
      deletionNoteText={<DeletionNoteText />}
      deletionNoteUrl={selectedContact && isSelf(selectedContact.user) ? null : deletionNoteUrl}
      autoResponseMessage={autoResponseMessage}
      header={
        <ChatMessageStreamHeader
          className="flex-initial my-auto"
          contact={chatUser}
          text={getHeaderText()}
        />
      }
      // eslint-disable-next-line react/jsx-no-bind
      audioSourceResolver={fetchAudioSource}
      isBasicUser={isBasicUser}
      onWriteExamination={canWriteExamination ? onWriteExamination : undefined}
    />
  );
};

ChatStreamModule.propTypes = {
  onScheduleDemo: PropTypes.func,
  isBasicUser: PropTypes.bool,
};

ChatStreamModule.defaultProps = {
  onScheduleDemo: null,
  isBasicUser: false,
};

export default ChatStreamModule;
